import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { PageLoader } from '@/design-system/components/ui';
import { PORTALS, type NavItem, type PortalKey } from '@/features/access/portals';
import { RequireAuth, RequirePermission, RequirePortal } from '@/features/auth/guards';
import { AuthLayout } from '@/layouts/AuthLayout';
import { PublicLayout } from '@/layouts/PublicLayout';
import { RouteErrorPage } from '@/pages/errors/RouteErrorPage';
import { PORTAL_SCREENS } from './portalScreens';
import { RootRoute } from './RootRoute';

// Route-level code splitting: every page loads as its own chunk.
const LandingPage = lazy(() => import('@/pages/public/LandingPage'));
const VehiclesPage = lazy(() => import('@/pages/public/VehiclesPage'));
const ServicesPage = lazy(() => import('@/pages/public/ServicesPage'));
const ProductsPage = lazy(() => import('@/pages/public/ProductsPage'));
const ProductFamilyPage = lazy(() => import('@/pages/public/ProductFamilyPage'));
const BookPage = lazy(() => import('@/pages/public/BookPage'));
const CentersPage = lazy(() => import('@/pages/public/CentersPage'));
const VehiclePreviewPage = lazy(() => import('@/pages/public/VehiclePreviewPage'));
const AuthenticityPage = lazy(() => import('@/pages/public/AuthenticityPage'));
const VerifyPage = lazy(() => import('@/pages/public/VerifyPage'));
const TrackPage = lazy(() => import('@/pages/public/TrackPage'));
const WarrantyPage = lazy(() => import('@/pages/public/InfoPages').then((m) => ({ default: m.WarrantyPage })));
const ContactPage = lazy(() => import('@/pages/public/InfoPages').then((m) => ({ default: m.ContactPage })));
const PrivacyPage = lazy(() => import('@/pages/public/InfoPages').then((m) => ({ default: m.PrivacyPage })));
const CreditsPage = lazy(() => import('@/pages/public/InfoPages').then((m) => ({ default: m.CreditsPage })));
const StoryPage = lazy(() => import('@/pages/public/StoryPage'));
// Sandbox test payments (development and staging): signed-in customers only.
const SandboxPayPage = lazy(() => import('@/pages/customer/SandboxPayPage'));
const NotFoundPage = lazy(() => import('@/pages/errors/ErrorPages').then((m) => ({ default: m.NotFoundPage })));
const ForbiddenPage = lazy(() => import('@/pages/errors/ErrorPages').then((m) => ({ default: m.ForbiddenPage })));

// Portal shell and generic portal pages load with the first portal visit, never with the public site.
const PortalLayout = lazy(() => import('@/layouts/PortalLayout').then((m) => ({ default: m.PortalLayout })));
const PortalHomePage = lazy(() => import('@/pages/portal/PortalHomePage').then((m) => ({ default: m.PortalHomePage })));
const ModuleFoundationPage = lazy(() =>
  import('@/pages/portal/ModuleFoundationPage').then((m) => ({ default: m.ModuleFoundationPage })),
);

const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const AuthCallbackPage = lazy(() => import('@/pages/auth/AuthCallbackPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const AuthConfirmPage = lazy(() => import('@/pages/auth/AuthConfirmPage'));
// Staff account activation (a link HR sends to a new employee's personal e-mail).
const ActivatePage = lazy(() => import('@/pages/auth/ActivatePage'));

/** Keeps the surrounding layout on screen while a page chunk loads. */
function withLoader(Page: ComponentType): ReactNode {
  return (
    <Suspense fallback={<PageLoader />}>
      <Page />
    </Suspense>
  );
}

/** Portals with their own shell ('tracking' is the public /track page). */
export const SHELL_PORTALS: readonly PortalKey[] = [
  'customer',
  'executive',
  'admin',
  'finance',
  'hr',
  'marketing',
  'workshop',
  'inventory',
  'logistics',
  'pos',
  'qa',
  'audit',
  'employee',
  'business',
  'vendor',
];

function screenFor(key: PortalKey, item: NavItem): ReactNode {
  const Screen = PORTAL_SCREENS[key]?.[item.path];
  if (item.path === '')
    return Screen ? (
      withLoader(Screen)
    ) : (
      <Suspense fallback={<PageLoader />}>
        <PortalHomePage portal={key} />
      </Suspense>
    );
  if (Screen && item.implemented && item.milestone !== 2) return withLoader(Screen);
  return (
    <Suspense fallback={<PageLoader />}>
      <ModuleFoundationPage item={item} />
    </Suspense>
  );
}

/**
 * One protected route tree per portal, generated from the portal registry:
 * RequirePortal gates the portal by role, RequirePermission gates each item.
 * Absolute nav links (e.g. /book) point at public routes and are skipped.
 */
function portalRoute(key: PortalKey): RouteObject {
  const definition = PORTALS[key];
  const children: RouteObject[] = definition.nav
    .filter((item) => !item.path.startsWith('/'))
    .map((item) => {
      const screen = screenFor(key, item);
      const element = item.permission ? <RequirePermission permission={item.permission}>{screen}</RequirePermission> : screen;
      return item.path === '' ? { index: true, element } : { path: item.path, element };
    });
  children.push({ path: '*', element: <Navigate to={definition.basePath} replace /> });
  return {
    path: definition.basePath.slice(1),
    element: (
      <RequirePortal portal={key}>
        <Suspense fallback={<PageLoader />}>
          <PortalLayout portal={key} />
        </Suspense>
      </RequirePortal>
    ),
    children,
  };
}

export const router = createBrowserRouter([
  {
    element: <RootRoute />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <PublicLayout />,
        children: [
          { index: true, element: withLoader(LandingPage) },
          { path: 'vehicles', element: withLoader(VehiclesPage) },
          { path: 'services', element: withLoader(ServicesPage) },
          { path: 'products', element: withLoader(ProductsPage) },
          { path: 'products/:code', element: withLoader(ProductFamilyPage) },
          { path: 'book', element: withLoader(BookPage) },
          { path: 'centers', element: withLoader(CentersPage) },
          { path: 'vehicle-preview', element: withLoader(VehiclePreviewPage) },
          { path: 'verify', element: withLoader(VerifyPage) },
          { path: 'authenticity', element: withLoader(AuthenticityPage) },
          { path: 'track', element: withLoader(TrackPage) },
          { path: 'warranty', element: withLoader(WarrantyPage) },
          { path: 'contact', element: withLoader(ContactPage) },
          { path: 'privacy', element: withLoader(PrivacyPage) },
          { path: 'credits', element: withLoader(CreditsPage) },
          { path: 'story', element: withLoader(StoryPage) },
          { path: 'pay/:intentNo', element: <RequireAuth>{withLoader(SandboxPayPage)}</RequireAuth> },
          { path: 'forbidden', element: withLoader(ForbiddenPage) },
          { path: '*', element: withLoader(NotFoundPage) },
        ],
      },
      {
        element: <AuthLayout />,
        children: [
          { path: 'login', element: withLoader(LoginPage) },
          { path: 'register', element: withLoader(RegisterPage) },
          { path: 'verify-email', element: withLoader(VerifyEmailPage) },
          { path: 'forgot-password', element: withLoader(ForgotPasswordPage) },
          { path: 'auth/callback', element: withLoader(AuthCallbackPage) },
          { path: 'auth/reset-password', element: withLoader(ResetPasswordPage) },
          { path: 'auth/confirm', element: withLoader(AuthConfirmPage) },
          { path: 'activate', element: withLoader(ActivatePage) },
        ],
      },
      ...SHELL_PORTALS.map(portalRoute),
    ],
  },
]);
